import { DataTableCustomized } from '@/components/ui/data-table-customized'
import { FilterBar } from '@/components/ui/data-table-customized'
import Statistical from './Statistical'
import { useCustomerInvoices } from '@/modules/Customer/hooks/use-customer-invoices'
import { useParams } from 'react-router-dom'
import Paper from '@/components/Paper'
import { INVOICE_STATUS } from '@/modules/Customer/constants'
import { useTourHandler } from '@/contexts/tour-handler'
import { useQuickHelp, QuickHelpOption } from '@/contexts/quick-help'
import { useMergeInvoicesTour } from '@/modules/Customer/hooks/use-merge-invoices-tour'
import { useEffect, useMemo } from 'react'

const Invoices = () => {
    const { id } = useParams()
    const { registerHandler, unregisterHandler } = useTourHandler()
    const { startTour: startMergeTour } = useMergeInvoicesTour()

    const {
        invoices,
        pagination,
        stateComponent,
        isFetching,
        params,
        control,
        setFilter,
        searchInputProps,
        columns,
        invoiceItemsColumns,
        selectionActions,
        filterItems,
        selectedInvoices,
        handleSelectionChange,
        handleSortChange,
        handlePageChange,
        handlePageSizeChange,
        t,
    } = useCustomerInvoices()

    // Quick Help options for merge invoices functionality
    const quickHelpOptions: QuickHelpOption[] = useMemo(
        () => [
            {
                id: 'select-invoices-to-merge',
                label: 'Chọn hóa đơn cần gộp',
                description: 'Cách chọn và gộp nhiều hóa đơn',
                selector:
                    '[data-tour="invoice-table"] tbody tr:first-child input[type="checkbox"]',
                tourTitle: 'Chọn Hóa Đơn Cần Gộp',
                tourDescription:
                    'Click vào checkbox để chọn các hóa đơn muốn gộp:\n• Chỉ có thể gộp hóa đơn ở trạng thái "Chờ Xác Nhận" hoặc "Đã Duyệt"\n• Phải chọn ít nhất 2 hóa đơn\n• Các hóa đơn đã chọn sẽ được đánh dấu màu xanh',
            },
            {
                id: 'merge-invoices-button',
                label: 'Nút gộp hóa đơn',
                description: 'Thực hiện gộp các hóa đơn đã chọn',
                selector:
                    '[data-tour="invoice-table"] button:has([data-testid="MergeIcon"])',
                tourTitle: 'Nút Gộp Hóa Đơn',
                tourDescription:
                    'Nhấn nút "Gộp Hóa Đơn" để:\n• Xem preview các dịch vụ sẽ được gộp\n• Kiểm tra tổng tiền\n• Xác nhận thao tác gộp',
            },
            {
                id: 'merge-preview',
                label: 'Xem trước kết quả gộp',
                description: 'Kiểm tra danh sách dịch vụ trước khi gộp',
                selector: '[role="dialog"]',
                tourTitle: 'Xác Nhận Gộp Hóa Đơn',
                tourDescription:
                    'Modal xác nhận hiển thị:\n• Danh sách tất cả dịch vụ từ các hóa đơn được chọn\n• Kỳ áp dụng và giá tiền\n• Tổng giá trị sau gộp\n\n⚠️ Thao tác này không thể hoàn tác!',
            },
        ],
        []
    )

    useQuickHelp(quickHelpOptions)

    // Register merge invoices tour handler
    useEffect(() => {
        registerHandler(startMergeTour)
        return () => unregisterHandler()
    }, [registerHandler, unregisterHandler, startMergeTour])

    return (
        <div className="space-y-2">
            {/* Statistical Component */}
            <Paper data-tour="invoices-statistical">
                <Statistical customerID={Number(id)} />
            </Paper>
            {/* Data Table */}
            <Paper data-tour="invoices-list" data-tour-merge="invoices-merge">
                <FilterBar
                    control={control}
                    setFilter={setFilter}
                    items={filterItems}
                    hideSearch
                />
                {stateComponent || (
                    <DataTableCustomized
                        data-tour="invoice-table"
                        columns={columns}
                        rows={invoices}
                        rowKey={(row) => String(row?.invoice_id)}
                        size="small"
                        resizable
                        loading={!invoices.length && !pagination}
                        isFetching={isFetching}
                        emptyMessage={t('no-invoices', {
                            defaultValue: 'Không có hóa đơn nào',
                        })}
                        selection={{
                            selectedKeys: selectedInvoices,
                            onSelectionChange: handleSelectionChange,
                            getRowKey: (row) => String(row?.invoice_id),
                            canSelect: (row) =>
                                [
                                    INVOICE_STATUS.WaitingConfirm,
                                    INVOICE_STATUS.Approved,
                                ].includes(row?.invoice_status),
                            actions: selectionActions,
                        }}
                        sorting={{
                            sortBy: params.sort_by,
                            sortOrder: params.sort_order,
                            onSortChange: handleSortChange,
                        }}
                        expandable={{
                            expandedColumns: invoiceItemsColumns,
                            expandedRowData: (row) =>
                                (row.invoice_items || []).map((item) => ({
                                    ...item,
                                    invoice_sale_type: row.invoice_sale_type, // Pass parent invoice sale type
                                    invoice_payment_method:
                                        row.invoice_payment_method, // Pass parent payment method
                                })),
                            expandedRowKey: (item) => item.invoice_item_id,
                            rowExpandable: (row) =>
                                row.invoice_items &&
                                row.invoice_items.length > 0,
                        }}
                        pagination={
                            pagination
                                ? {
                                      page: params.page || 1,
                                      pageSize: params.page_size || 10,
                                      total: pagination.total || 0,
                                      onPageChange: handlePageChange,
                                      onPageSizeChange: handlePageSizeChange,
                                      pageSizeOptions: [10, 20, 50, 100],
                                  }
                                : undefined
                        }
                        toolbar={{
                            search: {
                                ...searchInputProps,
                                placeholder: t('search-invoice', {
                                    defaultValue: 'Tìm kiếm hóa đơn...',
                                }),
                                tourId: 'invoice-search',
                            },
                            settings: {
                                show: true,
                                storageKey: 'customer-invoices',
                            },
                        }}
                    />
                )}
            </Paper>
        </div>
    )
}

export default Invoices
